import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref, watch } from 'vue';
import { ArticleV1, NewArticleV1, Publisher } from '@ilteducation/content-registry-types';
import axios from 'axios';
import DisableableButton from '@/components/buttons/DisableableButton.vue';
import ArticleEditor from '@/components/article/ArticleEditor.vue';
import useMediaFormFeature from '@/comp/use-media-form-feature';
import ArticleCoverUploader from '@/components/ArticleCoverUploader.vue';
import ConfirmationDialog from '@/components/ConfirmationDialog.vue';
import mediaProvider from '@/data-provider/mediaProvider';
import useDownloadMedia from '@/comp/use-download-media';
import useUploadMedia from '@/comp/use-upload-media';
import store from '@/store';
import { getAvailablePublishers, getArticleTags } from '@/data-provider/refDataProvider';
import DropFile from '@/components/base/DropFile.vue';
import AutocompleteInput from '@/components/base/AutocompleteInput.vue';
import { createAudio } from '@/foundations/helpers/articleAudio';
import eventBus from '@/foundations/services/eventBus';
import ComboBox from '@/components/base/ComboBox.vue';

type Props = {
  id?: string;
  title: string;
  item: ArticleV1 | NewArticleV1;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'Form',
  props: {
    id: null,
    title: null,
    item: null
  },
  setup(__props: any) {

const props = __props as {
  id?: string;
  title: string;
  item: ArticleV1 | NewArticleV1;
}

const isArticle = (content: NewArticleV1): content is ArticleV1 => content.type === 'ArticleV1' && content.id;


const notEmptyRule = ref([(val: any) => (val || '').length > 0 || 'This field is required']);
const trimmedString = ref([
  (val: string) => !/^\s+|\s+$/.test(val) || 'Leading or trailing whitespaces are not allowed',
]);
const availablePublishers = ref([] as Publisher[]);
getAvailablePublishers().then((publishers) => {
  availablePublishers.value = publishers
    .sort((a: Publisher, b: Publisher) => a.name.localeCompare(b.name))
    .map((pub: Publisher) => pub.name);
});

// Articles that predate the mediaSource array migration still hold a single string. Normalize
// before the inputs mount - they seed the form model straight from the item, bypassing v-model.
const rawMediaSource = props.item.mediaSource as string[] | string | null | undefined;
if (typeof rawMediaSource === 'string') {
  props.item.mediaSource = [rawMediaSource];
}

const availableTags = ref<string[]>([]);
onMounted(() => {
  getArticleTags().then((tags) => {
    availableTags.value = tags;
  });
});

const availableLang = [
  {
    text: 'Swedish',
    value: 'swe',
  },
  {
    text: 'English',
    value: 'en',
  },
];

const Article = ref<ArticleV1 | NewArticleV1>();
if (isArticle(props.item)) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, createdAt, state, lastUpdatedAt, ...rest } = props.item;
  Article.value = rest;
} else {
  Article.value = props.item;
}

const generateTS = async () => {
  console.log('Triggering TS job');
  await axios.post(`${process.env.VUE_APP_CONTENT_API_URL}${props.id}/textMarking`);
  eventBus.$emit('snackbar.open', 'Starting synthetic audio generation!');
};

const hasAudio = (): boolean => {
  return !!(props.item.audio && Object.keys(props.item.audio).length > 0);
};

const confirmDialog = ref<InstanceType<typeof ConfirmationDialog> | null>(null);

const confirmAndGenerateTS = async () => {
  if (hasAudio()) {
    const confirmed = await confirmDialog.value?.open(
      'Confirm Audio Generation',
      'This article already has audio. Are you sure you want to regenerate?',
      'No',
      'Yes',
    );
    if (!confirmed) return;
  }
  await generateTS();
};

const { saveButtonAvailable, isCloneMode } = useMediaFormFeature(props.item);
const { downloadMediaUrl, downloadMediaFile } = useDownloadMedia();
const { upload } = useUploadMedia();

const isPublished = ref<boolean>(false);
const publishClicked = (value: boolean) => {
  if (!props.item.isPublished && value) {
    isPublished.value = true;
  }
};

const previewLink = () => {
  if (props?.item?.id) {
    return `https://app${process.env.VUE_APP_ENVIRONMENT === 'production' ? '' : '-dev'}.trovy.se/view/${
      props?.item?.id
    }`;
  }
  return null;
};

const articleBody = ref<string>('');
const loadingArticleBody = ref(true);
const articleBodyUpdated = ref<boolean>(false);

const coverFile = ref<File | null>(null);
const coverFileUpdated = ref<boolean>(false);
const posterFile = ref<File | null>(null);
const posterFileUpdated = ref<boolean>(false);
const articleAudioFile = ref<File | null>(null);
const articleAudioFileUpdated = ref<boolean>(false);

const createImage = async (file: File, type: 'Poster' | 'Cover' | 'Inline') => {
  const response = await store.dispatch('content-image/create', {
    data: { description: `article: ${props.id} type: ${type}`, type: 'ImageV1', realm: 'all' },
  });
  // content-registry validates the extension case sensitively and stores the file as
  // image.<extension>, so normalise it before uploading and when building the url.
  const fParts = file.name.split('.');
  const fExt = fParts[fParts.length - 1].toLowerCase();
  const normalisedFile = new File([file], `${fParts.slice(0, -1).join('.')}.${fExt}`, { type: file.type });
  await upload(response.data.id, normalisedFile);
  const cfUrl = `https://${process.env.VUE_APP_COVERS_DOMAIN}/images/${response?.data?.id}/image.${fExt}`;
  if (response?.data?.id && (type === 'Cover' || type === 'Poster')) {
    if (type === 'Cover') {
      const data = [];
      if (!props.item?.cover) {
        data.push({ op: 'add', path: '/cover', value: {} });
      }
      data.push({ op: 'add', path: '/cover/url', value: cfUrl });
      await store.dispatch('articles/update', {
        id: props.id,
        data,
      });
    }
    if (type === 'Poster') {
      await store.dispatch('articles/update', {
        id: props.id,
        data: [{ op: 'replace', path: '/posterUrl', value: cfUrl }],
      });
    }
  }
  return cfUrl;
};
const showOverlay = ref(false);
const uploadArticleContent = async () => {
  showOverlay.value = true;
  try {
    if (props.id) {
      if (articleBodyUpdated.value) {
        const file = new File([articleBody.value], 'article.html', { type: 'text/html', lastModified: Date.now() });
        await mediaProvider.uploadMedia(props.id, file, { subType: 'content' });
        articleBodyUpdated.value = false;
      }
      const imageUpdated = posterFileUpdated.value || coverFileUpdated.value;
      if (posterFileUpdated.value) {
        await createImage(posterFile.value!, 'Poster');
        posterFileUpdated.value = false;
      }
      if (coverFileUpdated.value) {
        await createImage(coverFile.value!, 'Cover');
        coverFileUpdated.value = false;
      }
      if (imageUpdated) {
        /* createImage writes the new url with a patch of its own, which does not touch the
           local item, and va-form then redirects to the route we are already on - a
           duplicate navigation that never re-runs the route's fetch. Refresh the item here
           or the new url only reaches the uploader on a manual page reload. */
        await store.dispatch('articles/refresh');
      }
      if (articleAudioFileUpdated.value && isArticle(props.item)) {
        await createAudio(props.item, articleAudioFile.value!);
        articleAudioFileUpdated.value = false;
      }
    } else {
      console.log('Cant save content to not created article');
    }
    // !!HACK!! if this is an english article without audio and we are publishing it, lets generate audio for it
    if (props.item.content?.language === 'en' && isPublished.value && !props.item.audio?.hasOwnProperty('en-GB')) {
      await generateTS();
    }
  } catch (e) {
    console.error('Error uploading article content', e);
  } finally {
    showOverlay.value = false;
  }
};

const loadArticleContent = async () => {
  if (props.id && props.item?.contentMeta?.path) {
    // Support for loading article content from Elsa
    if (props.item.contentMeta?.path.startsWith('http')) {
      const elsaResp = await fetch(`${props.item.contentMeta.path}/article.html`);
      articleBody.value = await elsaResp.text();
    } else {
      const baseUrl = await downloadMediaUrl(props.id, { allContent: true });
      articleBody.value = await downloadMediaFile(`${baseUrl}/article.html`);
    }
  }
  loadingArticleBody.value = false;
  // Add watch on body here to not set this on initial load
  watch([articleBody], async () => {
    articleBodyUpdated.value = true;
  });
};
loadArticleContent();

const updateCoverFile = (file: File) => {
  coverFile.value = file;
  coverFileUpdated.value = true;
};
const updatePosterFile = (file: File) => {
  posterFile.value = file;
  posterFileUpdated.value = true;
};
const updateAudioFiles = (dataFiles: File[]) => {
  // eslint-disable-next-line prefer-destructuring
  articleAudioFile.value = dataFiles[0];
  articleAudioFileUpdated.value = true;
};

return { __sfc: true,isArticle, props, notEmptyRule, trimmedString, availablePublishers, rawMediaSource, availableTags, availableLang, Article, generateTS, hasAudio, confirmDialog, confirmAndGenerateTS, saveButtonAvailable, isCloneMode, downloadMediaUrl, downloadMediaFile, upload, isPublished, publishClicked, previewLink, articleBody, loadingArticleBody, articleBodyUpdated, coverFile, coverFileUpdated, posterFile, posterFileUpdated, articleAudioFile, articleAudioFileUpdated, createImage, showOverlay, uploadArticleContent, loadArticleContent, updateCoverFile, updatePosterFile, updateAudioFiles, DisableableButton, ArticleEditor, ArticleCoverUploader, ConfirmationDialog, DropFile, AutocompleteInput, ComboBox }
}

})